import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Tooltip,
  IconButton,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import { get, postJsonData } from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import Spinner from "../../component/loading-screen/Spinner";
import CloseIcon from "@mui/icons-material/Close";
import ModalFooter from "../../modals/ModalFooter";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%", // responsive: default to 90% on small screens
  maxWidth: 720, // cap at 720px
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  p: 4,
  height: "max-content",
  overflowY: "auto",
  borderRadius: "10px",
};

const EditUser = ({ open, handleClose, userData, refresh }) => {
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState(false);

  const [roles, setRoles] = useState([]);
  const [subRoles, setSubRoles] = useState([]);
  const [rolesFetched, setRolesFetched] = useState(false);
  const [subRolesFetched, setSubRolesFetched] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    username: "",
    mobile: "",
    email: "",
    gender: "",
    role: "",
    sub_role: "",
    status: "",
    establishment: "",
    in_time: "",
    out_time: "",
  });

  // sync userData into formData when modal opens or userData changes
  useEffect(() => {
    if (userData && open) {
      setFormData({
        id: userData?.id || "",
        name: userData?.name || "",
        username: userData?.username || "",
        mobile: userData?.mobile || "",
        email: userData?.email || "",
        gender: userData?.gender || "",
        role: userData?.role || "",
        sub_role: userData?.sub_role || "",
        status: userData?.status || "",
        establishment: userData?.profile?.establishment || "",
        in_time:
          userData?.role !== "Merchant" && userData?.role !== "Admin"
            ? userData?.in_time || ""
            : "",
        out_time:
          userData?.role !== "Merchant" && userData?.role !== "Admin"
            ? userData?.out_time || ""
            : "",
      });
      // optionally prefetch roles
      getRoleList("role");
      getRoleList("subrole");
    }
  }, [userData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getRoleList = useCallback(
    async (type) => {
      let query = type === "role" ? "type=Role" : "type=SubRole";
      let url = `${ApiEndpoints.GET_UTILS}?${query}`;

      if (type === "role" && rolesFetched) return;
      if (type === "subrole" && subRolesFetched) return;

      setRequest(true);

      get(
        url,
        "",
        setRequest,
        (res) => {
          const list = res?.data?.data || [];
          if (type === "role") {
            setRoles(list.map((item) => item.name));
            setRolesFetched(true);
          } else {
            setSubRoles(list.map((item) => item.name));
            setSubRolesFetched(true);
          }
        },
        (err) => {
          apiErrorToast(err);
        }
      );
    },
    [rolesFetched, subRolesFetched]
  );

  const updateUser = useCallback(async () => {
    if (!formData.id) {
      apiErrorToast("User id missing");
      return;
    }
    setLoading(true);

    postJsonData(
      `${ApiEndpoints.UPDATE_USERS_BIGG}/${formData.id}`,
      formData,
      setLoading,
      (res) => {
        okSuccessToast(res?.data?.message || "User updated");
        handleClose();
        if (typeof refresh === "function") refresh();
      },
      (err) => {
        apiErrorToast(err);
        setLoading(false);
      }
    );
  }, [formData, handleClose, refresh]);

  const handleSubmit = (e) => {
    e.preventDefault();
    updateUser();
  };

  return (
    <Modal open={!!open} onClose={handleClose}>
      <Box sx={style}>
        <Spinner loading={loading} />

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            borderBottom: "1px solid #ccc",
            pb: 1,
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>Edit User</Typography>
          <Spinner loading={request} />
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit} id="edit-user-form">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography>Name</Typography>
              <TextField fullWidth name="name" value={formData.name} onChange={handleChange} required />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography>Username</Typography>
              <TextField fullWidth name="username" value={formData.username} onChange={handleChange} required />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography>Mobile</Typography>
              <TextField fullWidth type="tel" name="mobile" value={formData.mobile} onChange={handleChange} required />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography>Email</Typography>
              <TextField fullWidth name="email" type="email" value={formData.email} onChange={handleChange} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography>Gender</Typography>
              <TextField fullWidth name="gender" value={formData.gender} onChange={handleChange} />
            </Grid>

            {/* ROLE */}
            <Grid item xs={12} sm={6}>
              <Typography>Role</Typography>
              <TextField
                select
                fullWidth
                name="role"
                value={formData.role}
                onChange={handleChange}
                onClick={() => getRoleList("role")}
              >
                {roles.map((role, i) => (
                  <MenuItem key={i} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* SUBROLE (conditionally shown based on selected role) */}
            {["Developer", "DEVELOPER", "developer", "Trainee", "Backend"].includes(formData.role) && (
              <Grid item xs={12} sm={6}>
                <Typography>Sub Role</Typography>
                <TextField
                  select
                  fullWidth
                  name="sub_role"
                  value={formData.sub_role}
                  onChange={handleChange}
                  onClick={() => getRoleList("subrole")}
                >
                  {subRoles.map((s, i) => (
                    <MenuItem key={i} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* TIME FIELDS */}
            {["Trainee", "Backend", "Developer"].includes(formData.role) && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography>In Time</Typography>
                  <TextField name="in_time" fullWidth value={formData.in_time} onChange={handleChange} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography>Out Time</Typography>
                  <TextField name="out_time" fullWidth value={formData.out_time} onChange={handleChange} />
                </Grid>
              </>
            )}

            {/* MERCHANT ESTABLISHMENT */}
            {formData.role === "Merchant" && (
              <Grid item xs={12}>
                <Typography>Establishment</Typography>
                <TextField fullWidth name="establishment" value={formData.establishment} onChange={handleChange} />
              </Grid>
            )}

            {/* STATUS */}
            <Grid item xs={12} sm={6}>
              <Typography>Status</Typography>
              <TextField select fullWidth name="status" value={formData.status} onChange={handleChange}>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box mt={2}>
            <ModalFooter form="edit-user-form" request={loading} btn="Update" />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditUser;
